import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { optimizeImage, generateSrcSet } from '../utils/imageOptimization';
import { getCurationImageAlt, getImageAlt } from '../utils/imageAlt';
import { prefetchPage } from '../services/contentService';

export default function CurationCard({ curation, index, headingLevel = 2, priority = false }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef(null);
  const title = curation.title || 'Curated online course collection';
  const imageAlt = getImageAlt(curation.imageAlt, getCurationImageAlt(title));
  const HeadingTag = headingLevel === 3 ? 'h3' : 'h2';
  const warmPageCache = () => prefetchPage(curation.slug);

  useEffect(() => {
    if (imgRef.current?.complete) setIsLoaded(true);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.45, delay: Math.min(index * 0.05, 0.15) }}
      className="[content-visibility:auto] [contain-intrinsic-size:400px_620px]"
    >
      <Link
        to={`/courses/${curation.slug}`}
        className="group flex flex-col gap-4"
        onPointerEnter={warmPageCache}
        onFocus={warmPageCache}
        onTouchStart={warmPageCache}
      >
        <div
          className="relative overflow-hidden rounded-[2rem] shadow-sm border border-[#E5E0D8] bg-[#F0EEEB]"
          title={imageAlt}
        >
          <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors z-10 pointer-events-none" />
          <img
            ref={imgRef}
            src={optimizeImage(curation.image)}
            srcSet={generateSrcSet(curation.image)}
            sizes="(max-width: 639px) calc(100vw - 48px),(max-width: 1023px) calc(50vw - 40px),352px"
            width="800"
            height="1000"
            alt={imageAlt}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            fetchpriority={priority ? 'high' : undefined}
            onLoad={() => setIsLoaded(true)}
            className={`w-full aspect-[4/5] object-cover transform group-hover:scale-105 transition-[opacity,transform] duration-700 ease-out bg-[#F0EEEB] ${isLoaded ? 'opacity-100 scale-100' : 'opacity-70 scale-[1.02]'}`}
          />
        </div>
        <div className="px-2">
          <span className="inline-block text-[10px] font-bold text-[#8A847A] uppercase tracking-widest mb-2">
            {curation.tag}
          </span>
          <HeadingTag className="font-serif text-xl md:text-2xl text-[#2C2A29] leading-snug group-hover:text-[#8B7355] transition-colors">
            {title}
          </HeadingTag>
          <p className="text-sm text-[#6B6661] mt-2 line-clamp-2 leading-relaxed">
            {curation.description}
          </p>
        </div>
      </Link>
    </motion.div>
  );
}